import React, { useCallback, useEffect, useRef, useState } from "react";
import { Spinner, AlertMessage, MUIIcon } from "../../../widgets";
import {
    useDeleteSPFolderMutation,
    useDeleteSPFileMutation,
    useUploadDocument
} from "../../../services/data/sharepoint";
import {
    useCreateEntityChecklistItemMutation,
    useDeleteEntityChecklistItemMutation,
    useLazyGetEntityChecklistItemsQuery,
    useUpdateEntityChecklistItemMutation
} from "./services/entityChecklistItemsApi";
import { IEntityChecklistItem } from "./models";
import { useFormikContext } from "formik";
import { IEntity } from "../../../base/types";
import clsx from "clsx";

interface FileUploaderProps {
    relativePath: string;
    onUploadComplete?: (results: UploadedFileResult[]) => void;
    className?: string;
    entity?: string;
    entityId?: string;
    readonly?: boolean;
    required?: boolean;
    fieldId: string;
}

interface UploadedFileResult {
    fileName: string;
    folder: string;
    sharepointUrl: string;
}

export const MultiTypeDocument: React.FC<FileUploaderProps> = ({
    relativePath,
    entity,
    entityId,
    onUploadComplete,
    readonly,
    required,
    className,
    fieldId
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { uploadDocument } = useUploadDocument();
    const [createRecord] = useCreateEntityChecklistItemMutation();
    const [deleteSPFile] = useDeleteSPFileMutation();
    const [deleteChecklistItem] = useDeleteEntityChecklistItemMutation();
    const [updateRecord] = useUpdateEntityChecklistItemMutation();
    const [deleteSPFolder, { isLoading: isDeleting }] = useDeleteSPFolderMutation();
    const [getEntityChecklistItems, {
        data: entityChecklistItems = [],
        isError: isEntityError
    }] = useLazyGetEntityChecklistItemsQuery();

    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
    const { setFieldValue } = useFormikContext<IEntity>();

    const isChecklistValid = useCallback(() => {
        if (!entityChecklistItems || !required) return;
        return entityChecklistItems?.length > 0 && entityChecklistItems.every(i => i.documentUrl);
    }, [entityChecklistItems, required]);

    useEffect(() => {
        if (entity && entityId) {
            getEntityChecklistItems({ entity, entityId });
        }
    }, [entity, entityId]);

    useEffect(() => {
        const valid = isChecklistValid();
        setFieldValue(fieldId, valid);
    }, [entityChecklistItems]);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, reuploadItem?: IEntityChecklistItem) => {
        const files = event.target.files;
        if (!files || !entity || !entityId || !relativePath) return;

        setIsUploading(true);
        setError(null);

        try {
            if (reuploadItem) {
                // Single file reupload for rejected item
                const file = files[0];
                const folder = `${relativePath}/${file.name.split('.').slice(0, -1).join('')}`;
                const res = await uploadDocument({ file, folderRelativeUrl: relativePath });
                const url = res.serverAbsoluteUrl || res.serverRelativeUrl;

                await updateRecord({
                    id: reuploadItem.id,
                    entity: {
                        ...reuploadItem,
                        documentUrl: url,
                        rejectedReason: "Resubmitted",
                        description: file.name,
                        entityType: entity,
                        entityId
                    }
                }).unwrap();
            } else {
                // Multiple file upload
                for (const file of Array.from(files)) {
                    const folder = `${relativePath}/${file.name.split('.').slice(0, -1).join('')}`;
                    const res = await uploadDocument({ file, folderRelativeUrl: relativePath });
                    const url = res.serverAbsoluteUrl || res.serverRelativeUrl;

                    await createRecord({
                        entityType: entity,
                        entityId,
                        documentUrl: url,
                        description: file.name,
                    }).unwrap();
                }
            }

            getEntityChecklistItems({ entity, entityId });
            if (inputRef.current) inputRef.current.value = "";
        } catch (err) {
            console.error(err);
            setError(`Upload failed`);
        }

        setIsUploading(false);
    };


    const handleChecklistDelete = async (item: IEntityChecklistItem) => {
        try {
            setDeletingItemId(item.id);

            await deleteSPFile(item.documentUrl).unwrap();

            if (item.rejected) {
                await updateRecord({
                    id: item.id,
                    entity: {
                        ...item,
                        documentUrl: '',
                        entityType: entity,
                        entityId
                    }
                }).unwrap();
            } else {
                setDeletingItemId(item.id);
                await deleteSPFile(item.documentUrl).unwrap();
                // const folder = `${relativePath}/${item.description.split('.').slice(0, -1).join('')}`;
                // await deleteSPFolder(folder).unwrap();
                await deleteChecklistItem(item.id).unwrap();

                if (entity && entityId) {
                    getEntityChecklistItems({ entity, entityId });
                }
            }

            if (entity && entityId) {
                await getEntityChecklistItems({ entity, entityId });
            }
        } catch (err) {
            console.error(err);
            setError(`Failed to delete: ${item.description}`);
        } finally {
            setDeletingItemId(null);
            const stillValid = entityChecklistItems.some(i => i.documentUrl);
            setFieldValue(fieldId, stillValid);
        }
    };

    const getFileName = (url: string) => (url || '').split('/').pop();

    const fileUrl = (documentUrl: string) => {
        if (documentUrl?.toLowerCase().startsWith("https://")) {
            const url = new URL(documentUrl);
            documentUrl = `${url.pathname}${url.search}`;
        }
        return `/api/documents/document?url=${encodeURIComponent(documentUrl)}`;
    };

    return (
        <div className={className}>
            {!readonly && (
                <input
                    type="file"
                    ref={inputRef}
                    onChange={(e) => handleFileChange(e)}
                    multiple
                    accept=".pdf,.jpeg,.jpg,.png,.bmp,.doc,.docx,.zip"
                    className="form-control mb-3"
                />
            )}

            {isUploading && <Spinner show message="Uploading..." />}
            {(error || isEntityError) && (
                <AlertMessage show type="Error" message={error || "Failed to load uploaded documents"} />
            )}

            <ul className="list-group">
                {entityChecklistItems.map((item, index) => (
                    <li key={`existing-${index}`} className="list-group-item d-flex justify-content-between align-items-center flex-wrap">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            {item.documentUrl ? (
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href={fileUrl(item.documentUrl)}
                                    className="btn btn-link text-hover-info text-primary p-0 fs-8 fw-bold"
                                >
                                    <MUIIcon className="fs-6 me-1" iconName="CloudDownloadOutlined" />
                                    {getFileName(item.documentUrl)}
                                </a>
                            ) : (
                                (item.rejected && readonly && !item.documentUrl) && (
                                    <input
                                        type="file"
                                        accept=".pdf,.jpeg,.jpg,.png,.bmp,.doc,.docx,.zip"
                                        onChange={(e) => handleFileChange(e, item)}
                                        className="form-control form-control-sm"
                                    />
                                )
                            )}
                            {item.documentUrl && <span className="badge bg-success">Uploaded</span>}

                            {(item.rejected && readonly) ? (
                                <>
                                    <span className="badge bg-danger fw-semibold">Rejected</span>
                                    <label className="form-label fs-8 mb-0 d-flex align-items-center gap-1">
                                        <span className="fw-semibold text-muted">Reason:</span>
                                        <span className="text-danger fst-italic">{item.rejectedReason}</span>
                                    </label>

                                    {item.documentUrl && (
                                        <button
                                            type="button"
                                            disabled={deletingItemId === item.id}
                                            className="btn btn-link text-danger text-hover-primary p-0 fs-8 fw-bold"
                                            title="Delete the document"
                                            onClick={() => handleChecklistDelete(item)}
                                        >
                                            {deletingItemId === item.id && <Spinner show />}
                                            <MUIIcon className="fs-6 me-1" iconName="DeleteOutlineOutlined" />
                                            Delete
                                        </button>
                                    )}
                                </>
                            ) : <>
                                {(!readonly) && (
                                    <button
                                        type="button"
                                        disabled={deletingItemId === item.id}
                                        className="btn btn-link text-danger text-hover-primary p-0 fs-8 fw-bold"
                                        title="Delete the document"
                                        onClick={() => handleChecklistDelete(item)}
                                    >
                                        {deletingItemId === item.id && <Spinner show />}
                                        <MUIIcon className="fs-6 me-1" iconName="DeleteOutlineOutlined" />
                                        Delete
                                    </button>
                                )}
                            </>}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};
